import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// Backend-persisted notifications (lead/deal/task/ticket assignment, etc. —
// written by NotificationService.notifyUser) were never fetched by the
// frontend at all — the bell only ever showed ephemeral, socket-pushed
// inbox/chat events via `addNotification`. A real assignment notification
// was created in the database and then simply never seen by anyone.
const normalize = (n) => ({
  id: n._id,
  title: n.title,
  message: n.message,
  type: n.type,
  link: n.link,
  read: n.isRead,
  timestamp: n.createdAt,
  persisted: true,
});

export const fetchNotifications = createAsyncThunk(
  'notifications/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/notifications', { params: { limit: 50 } });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load notifications');
    }
  }
);

export const markNotificationReadRemote = createAsyncThunk(
  'notifications/markReadRemote',
  async (id, { rejectWithValue }) => {
    try {
      await api.put(`/notifications/${id}/read`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark as read');
    }
  }
);

export const markAllNotificationsReadRemote = createAsyncThunk(
  'notifications/markAllReadRemote',
  async (_, { rejectWithValue }) => {
    try {
      await api.put('/notifications/read-all');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to mark all as read');
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  criticalCount: 0,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action) => {
      const notification = {
        ...action.payload,
        id: Date.now() + Math.random(),
        timestamp: new Date(),
        read: false,
      };
      state.notifications.unshift(notification);
      state.unreadCount += 1;
      if (notification.type === 'critical') {
        state.criticalCount += 1;
      }
    },
    markNotificationAsRead: (state, action) => {
      const notification = state.notifications.find(n => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
        if (notification.type === 'critical') {
          state.criticalCount = Math.max(0, state.criticalCount - 1);
        }
      }
    },
    markAllNotificationsAsRead: (state) => {
      state.notifications.forEach(n => {
        if (!n.read) {
          n.read = true;
        }
      });
      state.unreadCount = 0;
      state.criticalCount = 0;
    },
    removeNotification: (state, action) => {
      const idx = state.notifications.findIndex(n => n.id === action.payload);
      if (idx > -1) {
        const notification = state.notifications[idx];
        state.notifications.splice(idx, 1);
        if (!notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
          if (notification.type === 'critical') {
            state.criticalCount = Math.max(0, state.criticalCount - 1);
          }
        }
      }
    },
    clearOldNotifications: (state) => {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      state.notifications = state.notifications.filter(n => new Date(n.timestamp) > oneDayAgo);
    },
    updateJobNotification: (state, action) => {
      const { jobId, status, metrics } = action.payload;
      const notification = state.notifications.find(n => n.jobId === jobId);
      if (notification) {
        notification.status = status;
        notification.metrics = metrics;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        const fetched = (action.payload.notifications || []).map(normalize);
        const existingIds = new Set(state.notifications.map(n => n.id));
        // Merge rather than replace — keeps any ephemeral socket-pushed
        // entries (inbox/chat alerts) that aren't backed by this collection.
        const merged = [...state.notifications, ...fetched.filter(n => !existingIds.has(n.id))];
        merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        state.notifications = merged;
        state.unreadCount = merged.filter(n => !n.read).length;
      })
      .addCase(markNotificationReadRemote.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.read) {
          notification.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllNotificationsReadRemote.fulfilled, (state) => {
        state.notifications.forEach(n => { n.read = true; });
        state.unreadCount = 0;
        state.criticalCount = 0;
      });
  },
});

export const {
  addNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  removeNotification,
  clearOldNotifications,
  updateJobNotification,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
